import React, { useCallback, useEffect, useRef, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import TextEditor from "../../common/TextEditor/TextEditor";
import InfoAlert from "../../common/InfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { stripHtml } from "../../../utils/avatarUtils";
import {
  ParticipantAccessGate,
  useParticipantAccess,
} from "./participantAccess";
import {
  fetchPaymentEmailTemplate,
  savePaymentEmailTemplate,
} from "./participantService";
import "../capa/CapaActionPlanningEmailConfigEditor.scss";
import "./participantmanager.scss";

const PAYMENT_EMAIL_MERGE_TAGS = [
  {
    tag: "{StudyName}",
    entityName: "Study",
    description: "Replaced with the study name when the email is sent.",
  },
  {
    tag: "{VolunteerName}",
    entityName: "Participant",
    description: "Replaced with the participant name when the email is sent.",
  },
];

function isBodyEmpty(html) {
  return !stripHtml(String(html || "")).trim();
}

async function copyMergeTagToClipboard(tag) {
  const text = String(tag || "");
  if (!text) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_e) {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_e2) {
    return false;
  }
}

const PaymentEmailTemplate = () => {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { canViewPaymentEmailTemplate, loading: accessLoading } =
    useParticipantAccess();

  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [allowEmail, setAllowEmail] = useState(false);
  const [hasExistingTemplate, setHasExistingTemplate] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastCopied, setLastCopied] = useState("");
  const loadSeqRef = useRef(0);

  const loadTemplate = useCallback(async () => {
    if (!userData?.OrganizationId) return;
    const seq = ++loadSeqRef.current;
    setPageLoading(true);
    showLoader();
    try {
      const data = await fetchPaymentEmailTemplate(userData);
      if (seq !== loadSeqRef.current) return;
      const nextSubject = data.EmailSubject || "";
      const nextBody = data.EmailText || "";
      setSubject(nextSubject);
      setBodyHtml(nextBody);
      setAllowEmail(!!data.IsAllowEmail);
      setHasExistingTemplate(
        !!(String(nextSubject).trim() || !isBodyEmpty(nextBody)),
      );
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      showAlert({
        title: "Error",
        message: err?.message || "Unable to load payment email template.",
        type: "danger",
      });
    } finally {
      if (seq === loadSeqRef.current) {
        setPageLoading(false);
        hideLoader();
      }
    }
  }, [userData, showLoader, hideLoader, showAlert]);

  useEffect(() => {
    if (!canViewPaymentEmailTemplate || accessLoading) return;
    loadTemplate();
  }, [canViewPaymentEmailTemplate, accessLoading, loadTemplate]);

  const handleCopyTag = useCallback(
    async (tag) => {
      const ok = await copyMergeTagToClipboard(tag);
      if (ok) {
        setLastCopied(tag);
        window.setTimeout(() => {
          setLastCopied((prev) => (prev === tag ? "" : prev));
        }, 1600);
      } else {
        showAlert({
          title: "Copy failed",
          message: "Unable to copy tag. Please copy it manually.",
          type: "warning",
        });
      }
    },
    [showAlert],
  );

  const handleSave = async () => {
    if (!String(subject || "").trim()) {
      showAlert({
        title: "Validation",
        message: "Please enter an email subject.",
        type: "warning",
      });
      return;
    }
    if (isBodyEmpty(bodyHtml)) {
      showAlert({
        title: "Validation",
        message: "Please enter the email text.",
        type: "warning",
      });
      return;
    }
    setSaving(true);
    showLoader();
    try {
      const result = await savePaymentEmailTemplate(userData, {
        EmailSubject: subject,
        EmailText: bodyHtml,
        IsAllowEmail: allowEmail,
      });
      const wasUpdate = hasExistingTemplate;
      setHasExistingTemplate(true);
      showAlert({
        title: wasUpdate ? "Updated" : "Saved",
        message:
          result?.message ||
          (wasUpdate
            ? "Template has been updated successfully."
            : "Template has been saved successfully."),
        type: "success",
      });
    } catch (err) {
      showAlert({
        title: "Error",
        message: err?.message || "Unable to save payment email template.",
        type: "danger",
      });
    } finally {
      setSaving(false);
      hideLoader();
    }
  };

  const busy = pageLoading || saving;
  const saveLabel = hasExistingTemplate ? "Update" : "Save";

  return (
    <ParticipantAccessGate
      allow={canViewPaymentEmailTemplate}
      loading={accessLoading}
    >
      <div className="participant-manager-page pm-list-page pm-payment-email-page">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "Payment Email Template" },
          ]}
          actions={[
            {
              icon: "fas fa-refresh",
              label: "Refresh",
              onClick: loadTemplate,
              disabled: busy,
            },
          ]}
        />

        <div className="pm-payment-email-columns">
          {/* Section 1 — template editor + save */}
          <section
            className="pm-payment-email-section pm-payment-email-section--editor"
            aria-label="Payment email template"
          >
            <div className="pm-payment-email-section__scroll pm-thin-scroll">
              <div className="form-check form-switch pm-payment-email-allow">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="pm-payment-allow-email"
                  checked={allowEmail}
                  disabled={busy}
                  onChange={(e) => setAllowEmail(e.target.checked)}
                />
                <label
                  className="form-check-label"
                  htmlFor="pm-payment-allow-email"
                >
                  Enable payment notification emails
                </label>
              </div>

              <div className="capa-action-planning-email-config pm-payment-email-fields">
                <div className="mb-2">
                  <label
                    className="form-label capa-email-config-label"
                    htmlFor="pm-payment-subject"
                  >
                    Subject template
                  </label>
                  <input
                    id="pm-payment-subject"
                    type="text"
                    className="form-control capa-email-config-input"
                    value={subject}
                    disabled={busy}
                    spellCheck={false}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </div>

                <div className="pm-payment-email-body-field">
                  <label className="form-label capa-email-config-label">
                    Body template (rich text)
                  </label>
                  <TextEditor
                    value={bodyHtml}
                    onChange={setBodyHtml}
                    placeholder="Email body…"
                    height="100%"
                    minHeight="12rem"
                    showImageButton={false}
                    blockImages
                    disabled={busy}
                    className="capa-ap-email-quill-root"
                  />
                </div>
              </div>
            </div>

            <div className="pm-payment-email-section__footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={busy}
              >
                <i className="fas fa-save me-1" aria-hidden />
                {saving
                  ? hasExistingTemplate
                    ? "Updating…"
                    : "Saving…"
                  : saveLabel}
              </button>
            </div>
          </section>

          {/* Section 2 — merge tags */}
          <aside
            className="pm-payment-email-section pm-payment-email-section--tags capa-action-planning-email-config"
            aria-label="Merge tags"
          >
            <div className="capa-email-tags-card border rounded-3 capa-email-tags-card-inner capa-email-tags-card--compact h-100">
              <div className="capa-email-tags-card__header d-flex justify-content-between align-items-center flex-shrink-0">
                <span className="capa-email-config-label small mb-0">
                  Merge tags
                </span>
                {lastCopied ? (
                  <span className="capa-email-tags-card__hint text-success">
                    <i className="fas fa-check me-1" aria-hidden />
                    Copied
                  </span>
                ) : (
                  <span className="capa-email-tags-card__hint text-muted">
                    Click tag or copy
                  </span>
                )}
              </div>
              <div
                className="capa-email-tag-list flex-grow-1 min-h-0"
                role="list"
              >
                {PAYMENT_EMAIL_MERGE_TAGS.map((row) => (
                  <div key={row.tag} className="capa-email-tag-item" role="listitem">
                    <div className="capa-email-tag-item__top">
                      <button
                        type="button"
                        className="capa-email-merge-tag capa-email-merge-tag--item"
                        title={`Copy ${row.tag}`}
                        onClick={() => handleCopyTag(row.tag)}
                        disabled={busy}
                      >
                        {row.tag}
                      </button>
                      <button
                        type="button"
                        className="capa-email-tag-item__copy-btn"
                        title={`Copy ${row.tag}`}
                        aria-label={`Copy ${row.tag}`}
                        onClick={() => handleCopyTag(row.tag)}
                        disabled={busy}
                      >
                        <i className="fas fa-copy" aria-hidden />
                      </button>
                    </div>
                    <div className="capa-email-tag-item__meta">
                      <span className="capa-email-tag-item__entity">
                        {row.entityName}
                      </span>
                      <span className="capa-email-tag-item__desc">
                        {row.description}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <InfoAlert {...alertState} onClose={hideAlert} />
      </div>
    </ParticipantAccessGate>
  );
};

export default PaymentEmailTemplate;
